import React, { useState, useEffect } from 'react';
import { X, Plus, Check, Lock, Globe, FolderPlus } from 'lucide-react';
import apiService from '../services/apiService';

const AddToPlaylistModal = ({ movie, onClose, onSuccess }) => {
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingTo, setAddingTo] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        loadPlaylists();
    }, []);

    const loadPlaylists = async () => {
        try {
            setLoading(true);
            const response = await apiService.getUserPlaylists();

            if (response.success) {
                const playlistsWithStatus = await Promise.all(
                    response.data.map(async (playlist) => {
                        try {
                            const detailResponse = await apiService.getPlaylist(playlist.id);
                            const isInPlaylist = detailResponse.success &&
                                detailResponse.data.movies.some(m => m.id === movie.id);
                            return { ...playlist, isInPlaylist };
                        } catch {
                            return { ...playlist, isInPlaylist: false };
                        }
                    })
                );
                setPlaylists(playlistsWithStatus);
            }
        } catch (err) {
            console.error('Error loading playlists:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToPlaylist = async (playlistId) => {
        try {
            setAddingTo(playlistId);
            const response = await apiService.addMovieToPlaylist(playlistId, movie.id);

            if (response.success) {
                setPlaylists(playlists.map(p =>
                    p.id === playlistId ? { ...p, isInPlaylist: true } : p
                ));

                if (onSuccess) {
                    onSuccess();
                }
            } else {
                if (response.message && response.message.includes('déjà')) {
                    setPlaylists(playlists.map(p =>
                        p.id === playlistId ? { ...p, isInPlaylist: true } : p
                    ));
                }
            }
        } catch (err) {
            console.error('Error adding movie to playlist:', err);
            alert('Erreur lors de l\'ajout');
        } finally {
            setAddingTo(null);
        }
    };

    const handleCreateAndAdd = async (newPlaylistData) => {
        try {
            const createResponse = await apiService.createPlaylist(newPlaylistData);

            if (createResponse.success) {
                const newPlaylist = createResponse.data;
                await apiService.addMovieToPlaylist(newPlaylist.id, movie.id);
                setShowCreateModal(false);
                loadPlaylists();

                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (err) {
            console.error('Error creating playlist and adding movie:', err);
            alert('Erreur lors de la création');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
            {/* ✨ MODIFICATION : max-w-2xl au lieu de max-w-md */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-2xl w-full border border-white/10 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-white/10">
                    <div className="flex-1 pr-4">
                        <h3 className="text-2xl font-bold text-white mb-2">Ajouter à une playlist</h3>
                        <p className="text-sm text-gray-400 line-clamp-1">{movie.title}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg flex-shrink-0"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                        </div>
                    ) : playlists.length === 0 ? (
                        <div className="text-center py-10">
                            <FolderPlus className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400 mb-4">Aucune playlist</p>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-3 rounded-lg transition-all"
                            >
                                <Plus className="w-5 h-5" />
                                Créer ma première playlist
                            </button>
                        </div>
                    ) : (
                        /* ✨ MODIFICATION : Grid 2 colonnes au lieu d'une seule */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {playlists.map((playlist) => (
                                <button
                                    key={playlist.id}
                                    onClick={() => !playlist.isInPlaylist && handleAddToPlaylist(playlist.id)}
                                    disabled={playlist.isInPlaylist || addingTo === playlist.id}
                                    className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                                        playlist.isInPlaylist
                                            ? 'bg-green-600/10 border-green-500/30 cursor-default'
                                            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-purple-500/50'
                                    } ${addingTo === playlist.id ? 'opacity-50' : ''}`}
                                >
                                    {/* Icône */}
                                    <div className={`p-3 rounded-lg flex-shrink-0 ${
                                        playlist.isInPlaylist ? 'bg-green-600' : 'bg-white/10'
                                    }`}>
                                        {playlist.isInPlaylist ? (
                                            <Check className="w-6 h-6 text-white" />
                                        ) : (
                                            <Plus className="w-6 h-6 text-gray-400" />
                                        )}
                                    </div>

                                    {/* Infos */}
                                    <div className="flex-1 text-left min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-white font-bold truncate">{playlist.name}</p>
                                            {playlist.is_public ? (
                                                <Globe className="w-4 h-4 text-green-400 flex-shrink-0" />
                                            ) : (
                                                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            {playlist.isInPlaylist ? 'Déjà dans cette playlist' : `${playlist.movie_count} film${playlist.movie_count > 1 ? 's' : ''}`}
                                        </p>
                                    </div>

                                    {/* Loader */}
                                    {addingTo === playlist.id && (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500 flex-shrink-0"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer avec bouton créer */}
                {!loading && playlists.length > 0 && (
                    <div className="p-6 pt-0">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-4 rounded-lg transition-all border border-white/10"
                        >
                            <Plus className="w-5 h-5" />
                            Créer une nouvelle playlist
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de création rapide */}
            {showCreateModal && (
                <QuickCreatePlaylistModal
                    movieTitle={movie.title}
                    onClose={() => setShowCreateModal(false)}
                    onCreated={handleCreateAndAdd}
                />
            )}
        </div>
    );
};

// Modal de création rapide de playlist
const QuickCreatePlaylistModal = ({ movieTitle, onClose, onCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Le nom est obligatoire');
            return;
        }

        try {
            setLoading(true);
            setError('');

            await onCreated({
                name: name.trim(),
                description: description.trim() || null,
                is_public: isPublic,
            });
        } catch (err) {
            console.error('Error creating playlist:', err);
            setError('Erreur lors de la création');
            setLoading(false);
        }
    };

    return (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-10 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 max-w-md w-full border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-4">Créer une playlist</h3>

                <div className="bg-purple-600/10 border border-purple-500/30 rounded-lg p-3 mb-4">
                    <p className="text-purple-300 text-sm">
                        <span className="font-bold">{movieTitle}</span> sera ajouté automatiquement
                    </p>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Nom de la playlist *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Films d'action"
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                            maxLength={255}
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description (optionnelle)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ex: Mes films préférés"
                            rows={2}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                            maxLength={1000}
                        />
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
                        <input
                            type="checkbox"
                            id="quick_is_public"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="w-5 h-5 rounded border-white/20 bg-white/10 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="quick_is_public" className="flex-1">
                            <span className="text-white font-medium text-sm">Playlist publique</span>
                        </label>
                        {isPublic ? (
                            <Globe className="w-5 h-5 text-green-400" />
                        ) : (
                            <Lock className="w-5 h-5 text-gray-400" />
                        )}
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-lg transition-all"
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'Création...' : 'Créer et ajouter'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddToPlaylistModal;